/**
Copyright (c) 2022–2026 Podstack.ai
All rights reserved.
Proprietary and Confidential.
Unauthorized copying of this file, via any medium, is strictly prohibited.
This file is part of the Podstack Platform.
*/

import { create } from 'zustand';
import { Organization } from '../types';
import { organizationService, authService } from '../services/api';
import { queryClient } from '../lib/queryClient';
import { safeStorage } from '../utils/safeStorage';

interface OrgState {
  organizations: Organization[];
  selectedOrgId: string | null;
  isLoading: boolean;
  fetchOrganizations: () => Promise<void>;
  selectOrg: (id: string | null) => Promise<void>;
  createOrg: (data: { name: string; slug: string; billing_email?: string }) => Promise<Organization>;
}

export const useOrgStore = create<OrgState>()((set, get) => ({
  organizations: [],
  selectedOrgId: safeStorage.getItem('selected_org_id'),
  isLoading: false,

  fetchOrganizations: async () => {
    set({ isLoading: true });
    try {
      const res = await organizationService.list();
      const orgs: Organization[] = res.data.organizations || [];
      // drop a stale selection (e.g. removed from the org)
      const selected = get().selectedOrgId;
      if (selected && !orgs.some((o) => o.id === selected)) {
        safeStorage.removeItem('selected_org_id');
        set({ selectedOrgId: null });
      }
      set({ organizations: orgs, isLoading: false });
    } catch {
      set({ isLoading: false });
    }
  },

  selectOrg: async (id: string | null) => {
    if (id) {
      safeStorage.setItem('selected_org_id', id);
    } else {
      safeStorage.removeItem('selected_org_id');
    }
    set({ selectedOrgId: id });
    queryClient.clear(); // project lists etc. are org-scoped now
    try {
      await authService.updateProfile({ selected_organization_id: id ?? '' });
    } catch {
      // persistence best-effort; local switch already applied
    }
  },

  createOrg: async (data) => {
    const res = await organizationService.create(data);
    await get().fetchOrganizations();
    return res.data;
  },
}));

// Reset org state on logout so the next user (or a re-login by a different
// account) doesn't inherit the previous user's organizations/selection.
// localStorage's 'selected_org_id' is cleared separately by the caller.
export const resetOrgStore = () => {
  useOrgStore.setState({ organizations: [], selectedOrgId: null, isLoading: false });
};
